/**
 * Parametric glitch operations on `NesPpuState`.
 *
 * Each operation is a pure function `(state, params, rng) -> state` that
 * returns a new state (the underlying typed arrays are cloned). The UI
 * composes a fixed pipeline of these so glitches stack predictably for a
 * given seed.
 */

import {
    clonePpuState,
    NAMETABLE_TILES_X,
    NAMETABLE_TILES_Y,
    type NesPpuState,
} from "./nesPpu";
import { makeRng } from "./nesChrSynth";
import { isReservedBlack } from "./nesPalette";

export interface GlitchParams {
    nametableShift: { enabled: boolean; delta: number };
    nametableNoise: { enabled: boolean; probability: number };
    nametableScroll: { enabled: boolean; rows: number; cols: number };
    attributeRandomize: { enabled: boolean; probability: number };
    paletteHueShift: { enabled: boolean; delta: number };
    paletteSwap: { enabled: boolean; probability: number };
    chrBitFlip: { enabled: boolean; probability: number };
    oamNoise: { enabled: boolean; count: number };
    bankSwap: { enabled: boolean; bg: boolean; spr: boolean };
    mirroringSwap: { enabled: boolean };
    /** Seed shared by all probabilistic ops below so the result is reproducible. */
    seed: number;
}

export const DEFAULT_GLITCH_PARAMS: GlitchParams = {
    nametableShift: { enabled: false, delta: 16 },
    nametableNoise: { enabled: false, probability: 0.05 },
    nametableScroll: { enabled: false, rows: 0, cols: 4 },
    attributeRandomize: { enabled: false, probability: 0.1 },
    paletteHueShift: { enabled: false, delta: 16 },
    paletteSwap: { enabled: false, probability: 0.2 },
    chrBitFlip: { enabled: false, probability: 0.002 },
    oamNoise: { enabled: false, count: 16 },
    bankSwap: { enabled: false, bg: false, spr: false },
    mirroringSwap: { enabled: false },
    seed: 1337,
};

/** Random glitch stack for Randomize parameters; ranges match the NES Glitch panel sliders. */
export function randomGlitchParams(): GlitchParams {
    const u = (): number => Math.random();
    const coin = (p: number): boolean => u() < p;
    const rint = (a: number, b: number): number =>
        Math.floor(u() * (b - a + 1)) + a;
    const rfloat = (a: number, b: number): number => u() * (b - a) + a;

    const bankOn = coin(0.28);
    let bg = u() < 0.5;
    let spr = u() < 0.5;
    if (bankOn && !bg && !spr) {
        if (u() < 0.5) bg = true;
        else spr = true;
    }

    return {
        nametableShift: {
            enabled: coin(0.42),
            delta: rint(-128, 128),
        },
        nametableNoise: {
            enabled: coin(0.42),
            probability: Math.round(rfloat(0, 0.35) * 100) / 100,
        },
        nametableScroll: {
            enabled: coin(0.28),
            rows: rint(-30, 30),
            cols: rint(-32, 32),
        },
        attributeRandomize: {
            enabled: coin(0.45),
            probability: Math.round(rfloat(0, 0.5) * 100) / 100,
        },
        paletteHueShift: {
            enabled: coin(0.36),
            delta: rint(-32, 32),
        },
        paletteSwap: {
            enabled: coin(0.4),
            probability: Math.round(rfloat(0, 0.45) * 100) / 100,
        },
        chrBitFlip: {
            enabled: coin(0.32),
            probability: Math.round(rfloat(0, 0.05) * 2000) / 2000,
        },
        oamNoise: {
            enabled: coin(0.36),
            count: rint(0, 64),
        },
        bankSwap: {
            enabled: bankOn,
            bg,
            spr,
        },
        mirroringSwap: { enabled: coin(0.22) },
        seed: (Math.floor(u() * 0x1_0000_0000) | 0) >>> 0,
    };
}

/**
 * Apply the full glitch pipeline. Operations run in a deterministic order so
 * that the same params + seed always reproduce the same output.
 */
export function applyGlitches(
    state: NesPpuState,
    params: GlitchParams,
): NesPpuState {
    let s = state;
    const rng = makeRng(params.seed);
    if (params.bankSwap.enabled) s = bankSwap(s, params.bankSwap);
    if (params.mirroringSwap.enabled) s = mirroringSwap(s);
    if (params.nametableShift.enabled) s = nametableShift(s, params.nametableShift.delta);
    if (params.nametableScroll.enabled) {
        s = nametableScroll(s, params.nametableScroll.rows, params.nametableScroll.cols);
    }
    if (params.nametableNoise.enabled) s = nametableNoise(s, params.nametableNoise.probability, rng);
    if (params.attributeRandomize.enabled) {
        s = attributeRandomize(s, params.attributeRandomize.probability, rng);
    }
    if (params.paletteHueShift.enabled) s = paletteHueShift(s, params.paletteHueShift.delta);
    if (params.paletteSwap.enabled) s = paletteSwap(s, params.paletteSwap.probability, rng);
    if (params.chrBitFlip.enabled) s = chrBitFlip(s, params.chrBitFlip.probability, rng);
    if (params.oamNoise.enabled) s = oamNoise(s, params.oamNoise.count, rng);
    return s;
}

/* ------------------------------ ops ------------------------------ */

export function nametableShift(state: NesPpuState, delta: number): NesPpuState {
    const next = clonePpuState(state);
    const d = ((delta | 0) % 256 + 256) % 256;
    for (let i = 0; i < next.nametable.length; i++) {
        next.nametable[i] = (next.nametable[i] + d) & 0xFF;
    }
    return next;
}

export function nametableNoise(
    state: NesPpuState,
    probability: number,
    rng: () => number,
): NesPpuState {
    const next = clonePpuState(state);
    const p = Math.max(0, Math.min(1, probability));
    for (let i = 0; i < next.nametable.length; i++) {
        if (rng() < p) {
            next.nametable[i] = Math.floor(rng() * 256);
        }
    }
    return next;
}

export function nametableScroll(
    state: NesPpuState,
    rows: number,
    cols: number,
): NesPpuState {
    const next = clonePpuState(state);
    const W = NAMETABLE_TILES_X;
    const H = NAMETABLE_TILES_Y;
    const dr = ((rows | 0) % H + H) % H;
    const dc = ((cols | 0) % W + W) % W;
    if (dr === 0 && dc === 0) return next;
    const original = new Uint8Array(next.nametable);
    for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
            const sy = (y + H - dr) % H;
            const sx = (x + W - dc) % W;
            next.nametable[y * W + x] = original[sy * W + sx];
        }
    }
    return next;
}

export function attributeRandomize(
    state: NesPpuState,
    probability: number,
    rng: () => number,
): NesPpuState {
    const next = clonePpuState(state);
    const p = Math.max(0, Math.min(1, probability));
    for (let i = 0; i < next.attribute.length; i++) {
        let byte = next.attribute[i];
        for (let q = 0; q < 4; q++) {
            if (rng() < p) {
                const newSub = Math.floor(rng() * 4) & 3;
                const shift = q * 2;
                byte = (byte & ~(0b11 << shift)) | (newSub << shift);
            }
        }
        next.attribute[i] = byte;
    }
    return next;
}

export function paletteHueShift(state: NesPpuState, delta: number): NesPpuState {
    const next = clonePpuState(state);
    const d = ((delta | 0) % 64 + 64) % 64;
    for (let i = 0; i < next.palette.length; i++) {
        let v = (next.palette[i] + d) & 0x3F;
        // step past reserved black entries so the result is always renderable.
        let safety = 0;
        while (isReservedBlack(v) && safety < 8) {
            v = (v + 1) & 0x3F;
            safety++;
        }
        next.palette[i] = v;
    }
    return next;
}

export function paletteSwap(
    state: NesPpuState,
    probability: number,
    rng: () => number,
): NesPpuState {
    const next = clonePpuState(state);
    const p = Math.max(0, Math.min(1, probability));
    for (let i = 0; i < next.palette.length; i++) {
        if (rng() < p) {
            let v = Math.floor(rng() * 64) & 0x3F;
            let safety = 0;
            while (isReservedBlack(v) && safety < 8) {
                v = (v + 1) & 0x3F;
                safety++;
            }
            next.palette[i] = v;
        }
    }
    return next;
}

export function chrBitFlip(
    state: NesPpuState,
    probability: number,
    rng: () => number,
): NesPpuState {
    const next = clonePpuState(state);
    const p = Math.max(0, Math.min(1, probability));
    for (let t = 0; t < 2; t++) {
        const table = next.patternTables[t];
        for (let i = 0; i < table.length; i++) {
            let byte = table[i];
            for (let bit = 0; bit < 8; bit++) {
                if (rng() < p) byte ^= 1 << bit;
            }
            table[i] = byte;
        }
    }
    return next;
}

export function oamNoise(
    state: NesPpuState,
    count: number,
    rng: () => number,
): NesPpuState {
    const next = clonePpuState(state);
    const n = Math.max(0, Math.min(64, Math.floor(count)));
    for (let i = 0; i < n; i++) {
        const slot = Math.floor(rng() * 64);
        const o = slot * 4;
        next.oam[o] = Math.floor(rng() * 240); // y
        next.oam[o + 1] = Math.floor(rng() * 256); // tile
        next.oam[o + 2] = Math.floor(rng() * 256) & 0xE3; // attr (preserve unused bits as 0)
        next.oam[o + 3] = Math.floor(rng() * 256); // x
    }
    return next;
}

export function bankSwap(
    state: NesPpuState,
    swap: { bg: boolean; spr: boolean },
): NesPpuState {
    const next = clonePpuState(state);
    if (swap.bg) next.bgPatternTable = (next.bgPatternTable ^ 1) as 0 | 1;
    if (swap.spr) next.sprPatternTable = (next.sprPatternTable ^ 1) as 0 | 1;
    return next;
}

export function mirroringSwap(state: NesPpuState): NesPpuState {
    const next = clonePpuState(state);
    next.mirroring = next.mirroring === "horizontal" ? "vertical" : "horizontal";
    return next;
}
